import { useState } from "react";
import { useSetCustomerOwners } from "@/hooks/use-customer-owners";
import { useUpsertMonitoringSource } from "@/hooks/use-monitoring-sources";
import { useFeatureFlag } from "@/hooks/use-feature-flag";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { normalizeWebsite } from "@/lib/lead-csv";

/**
 * CRM-feltene i «Ny kunde»: kundeeier (kundekort v2) og nettside (signaler,
 * der kundekortet viser nettsiden og overvåkningen). Begge lagres etter at
 * kunden er opprettet, så en feil her stopper aldri selve opprettelsen.
 */
export function useNewCustomerCrmFields() {
  const ownersEnabled = useFeatureFlag("feature_crm_kundekort");
  const websiteEnabled = useFeatureFlag("feature_crm_signaler");
  const { activeUser } = useImpersonation();
  const setOwners = useSetCustomerOwners();
  const upsertSource = useUpsertMonitoringSource();
  const [setMeAsOwner, setSetMeAsOwner] = useState(true);
  const [website, setWebsite] = useState("");

  const normalizedWebsite = website.trim() ? normalizeWebsite(website) : null;
  const websiteInvalid = websiteEnabled && website.trim().length > 0 && normalizedWebsite === null;

  return {
    ownersEnabled,
    websiteEnabled,
    setMeAsOwner,
    setSetMeAsOwner,
    website,
    setWebsite,
    websiteInvalid,
    reset: () => {
      setSetMeAsOwner(true);
      setWebsite("");
    },
    afterCreate: (customerId: string) => {
      if (ownersEnabled && setMeAsOwner && activeUser?.id) {
        setOwners.mutate({ customerId, userIds: [activeUser.id] });
      }
      // Manuell kilde: Brønnøysund-synken overskriver den aldri. Uten isActive
      // avgjør status-regelen, og en ny kunde er kald, så overvåkningen står av.
      if (websiteEnabled && normalizedWebsite) {
        upsertSource.mutate({ customerId, source: "website", identifier: normalizedWebsite });
      }
    },
  };
}

export type NewCustomerCrmFields = ReturnType<typeof useNewCustomerCrmFields>;
